import type { AutocompleteProps } from '@_/ui';
import type { GlobalSearchSuggestion } from './types.ts';
import { useDebouncedValue } from '@mantine/hooks';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import { getReactQueryOptions } from '@_/utils-frontend';
import { publicApiQueryClient } from '#src/api.ts';

type OnOpenChangeParameters = Parameters<
  NonNullable<AutocompleteProps<GlobalSearchSuggestion>['onOpenChange']>
>;

export function useGlobalSearch() {
  const navigate = useNavigate();
  const routerStatus = useRouterState({ select: state => state.status });

  const popupRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [debouncedInputValue] = useDebouncedValue(inputValue.trim(), 150);

  const query = useQuery({
    ...getReactQueryOptions(publicApiQueryClient.searchSuggestions.list, {
      query: { search: debouncedInputValue }
    }),
    enabled: !!debouncedInputValue,
    placeholderData: keepPreviousData
  });

  const items = useMemo(() => {
    const data = query.data?.data;
    const searchStr = inputValue.trim();

    if (!searchStr || !data) {
      return [];
    }

    return [
      ...(data?.venues || []),
      ...(data?.topics || []),
      {
        object: 'inputValue' as const,
        inputValue: searchStr
      }
    ] satisfies GlobalSearchSuggestion[];
  }, [inputValue, query.data]);

  const handleOpenChange = useCallback(
    (open: OnOpenChangeParameters[0], eventDetails?: OnOpenChangeParameters[1]) => {
      if (!open && eventDetails) {
        const event = eventDetails.event;

        const clickedInsidePopup =
          eventDetails.reason === 'outside-press' &&
          containsEventTarget(popupRef.current, event.target);

        if (clickedInsidePopup) {
          eventDetails.cancel();
          return;
        }
      }

      setIsOpen(open);
    },
    []
  );

  const onSubmit = useCallback<React.SubmitEventHandler>(
    event => {
      event.preventDefault();

      if (inputValue.trim()) {
        setIsOpen(false);

        void navigate({
          from: '/{-$locale}',
          to: '/{-$locale}/venues',
          search: {
            search: inputValue.trim()
          }
        });
      }
    },
    [inputValue, navigate]
  );

  const close = useEffectEvent(() => setIsOpen(false));
  useEffect(() => {
    if (routerStatus === 'pending') {
      close();
    }
  }, [routerStatus]);

  const autocompleteProps = {
    items,
    itemToStringValue,
    onOpenChange: handleOpenChange,
    onValueChange: setInputValue,
    open: isOpen,
    openOnInputClick: true,
    value: inputValue
  } satisfies AutocompleteProps<GlobalSearchSuggestion>;

  return {
    autocompleteProps,
    onSubmit,
    popupRef
  };
}

function itemToStringValue(suggestion: GlobalSearchSuggestion | null): string {
  if (!suggestion) {
    return '';
  }

  switch (suggestion.object) {
    case 'inputValue':
      return suggestion.inputValue;
    case 'topic':
    case 'venue':
      return suggestion.name;
    default:
      throw new Error('unknown search suggestion type');
  }
}

function containsEventTarget(element: HTMLElement | null, target: EventTarget | null): boolean {
  return target instanceof Node && element?.contains(target) === true;
}
